import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { ReactComponent as QuestionIcon } from '../assets/icons/question.svg';
import { ReactComponent as ResetIcon } from '../assets/icons/reset.svg';
import { ReactComponent as SendIcon } from '../assets/icons/send.svg';
import { ReactComponent as StopIcon } from '../assets/icons/stop.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { useDarkMode } from '../contexts/DarkModeContext';
// import { ReactComponent as MicIcon } from '../assets/icons/mic.svg';

interface ChatInputProps {
  question: string;
  setQuestion: (value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleReset?: () => void;   // Optional
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  stopFetching: () => void;
  isLoading: boolean;
  isStreaming: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  autoResize: (isMobile: boolean) => void;
  isRounded: boolean;
  showResetButton?: boolean;
  isMobile: boolean;
  onOpenPredefined?: () => void;
  isChatActive?: boolean;
  showGuideButton?: boolean;
  onGuideClick?: () => void;
  toggleRecording?: () => void;
  isRecording?: boolean;
  audioLevel?: number;
}

const ChatInput: React.FC<ChatInputProps> = ({
  question,
  setQuestion,
  handleSubmit,
  handleReset,
  handleKeyDown,
  stopFetching,
  isLoading,
  isStreaming,
  inputRef,
  autoResize,
  isRounded,
  showResetButton = true,
  isMobile,
  onOpenPredefined,
  isChatActive,
  showGuideButton,
  onGuideClick,
  toggleRecording,
  isRecording = false,
  audioLevel = 0
}) => {
  const { t } = useLanguage();
  const { isDarkMode } = useDarkMode();
  return (
    <>
      <ChatInputContainer onSubmit={handleSubmit} $isMobile={isMobile}>
        <ChatInputArea $isMobile={isMobile} $isChatActive={isChatActive}>
          {isMobile && onOpenPredefined && !isChatActive && (
            <QuestionButton type="button" onClick={onOpenPredefined} aria-label={t.chatInput.openPredefinedQuestions}>
              <QuestionIcon aria-hidden />
            </QuestionButton>
          )}

          {showResetButton && handleReset && (
            <ResetButton type="button" onClick={handleReset} aria-label={t.chatInput.resetChat}>
              <ResetIcon aria-hidden />
            </ResetButton>
          )}

          {/* 모바일 채팅 중 왼쪽 가이드 버튼 */}
          {isMobile && showGuideButton && (
            <GuideButtonLeft type="button" onClick={onGuideClick} aria-label={t.common.userGuideTitle}>
              <QuestionIcon aria-hidden />
            </GuideButtonLeft>
          )}

          <InputWrapper>
            <StyledInput
              ref={inputRef}
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                autoResize(isMobile);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t.chatInput.placeholder}
              rows={1}
              $isRounded={isRounded}
              readOnly={isStreaming}
            />
            
            {/* 모바일과 데스크톱 모두 동일한 버튼 표시 로직 */}
            {isStreaming ? (
              /* 전송 중일 때는 중지 버튼 */
              <MicButtonWrapper>
                <MicButton
                  type="button"
                  onClick={stopFetching}
                  $isRecording={false}
                  $isDarkMode={isDarkMode}
                  title={t.chatInput.stopSending}
                  aria-label={t.chatInput.stopSending}
                  disabled={false}
                >
                  <StopIcon width="18" height="18" />
                </MicButton>
              </MicButtonWrapper>
            ) : question.trim().length > 0 ? (
              /* 텍스트가 있을 때는 보내기 버튼 */
              <MicButtonWrapper>
                <MicButton
                  type="submit"
                  onClick={undefined}
                  $isRecording={false}
                  $isDarkMode={isDarkMode}
                  title={t.chatInput.sendMessage}
                  aria-label={t.chatInput.sendMessage}
                  disabled={false}
                >
                  <SendIcon width="18" height="18" />
                </MicButton>
              </MicButtonWrapper>
            ) : (
              /* 텍스트가 없을 때는 음성녹음 버튼 */
              toggleRecording && (
                <MicButtonWrapper>
                  <MicButton
                    type="button"
                    onClick={toggleRecording}
                    $isRecording={isRecording}
                    $isDarkMode={isDarkMode}
                    title={t.chatInput.voiceRecording}
                    aria-label={isRecording ? t.chatInput.stopRecording : t.chatInput.voiceRecording}
                    disabled={false}
                  >
                    {isRecording ? (
                      <StopIcon width="18" height="18" />
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2Z" fill="currentColor" stroke="currentColor" strokeWidth="0.5"/>
                        <path d="M19 11C19 14.87 15.87 18 12 18C8.13 18 5 14.87 5 11H7C7 13.76 9.24 16 12 16C14.76 16 17 13.76 17 11H19Z" fill="currentColor"/>
                        <path d="M12 18V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="22" r="1.5" fill="currentColor"/>
                      </svg>
                    )}
                  </MicButton>
                </MicButtonWrapper>
              )
            )}
          </InputWrapper>

          {/* 데스크톱에서도 모바일처럼 InputWrapper 안에서 버튼 표시 */}
        </ChatInputArea>
      </ChatInputContainer>
      
      {/* 음성 녹음 중일 때 나타나는 시각화 효과 */}
      {isRecording && (
        <VoiceVisualizerOverlay>
          <VoiceVisualizer>
            <VisualizerText>
              {audioLevel < 5 ? t.chatInput.listenMessage : t.chatInput.recordingMessage}
            </VisualizerText>
            <AudioLevelIndicator>
              <AudioLevelBar $level={Math.min(audioLevel, 100)} />
              <AudioLevelText>{t.chatInput.voiceLevel}: {Math.round(audioLevel)}</AudioLevelText>
            </AudioLevelIndicator>
            <WaveContainer>
              {Array.from({ length: 30 }).map((_, i) => (
                <WaveBar 
                  key={i} 
                  $delay={i * 0.05} 
                  $intensity={Math.min(audioLevel / 20, 2)} 
                />
              ))}
            </WaveContainer>
            <VisualizerButton onClick={toggleRecording}>
              {t.chatInput.stopRecording}
            </VisualizerButton>
          </VoiceVisualizer>
        </VoiceVisualizerOverlay>
      )}
    </>
  );
};

export default ChatInput;

// const ChatInputContainer = styled.form<{ $isMobile: boolean }>`
//   padding: 0;
//   background: transparent;
//   width: 100%;
//   box-sizing: border-box;
//   flex: 1;
// `;
const ChatInputContainer = styled.form<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? '0.5rem 0.75rem' : '1rem 2rem')};
  background: transparent;
  border: none;
  width: 100%;
  box-sizing: border-box;

  /* 320px 화면에서 padding 조정 */
  @media (max-width: 320px) {
    padding: 0.5rem 0.5rem;
  }
`;

// const ChatInputArea = styled.div<{ $isMobile: boolean; $isChatActive?: boolean }>`
//   display: flex;
//   align-items: center;
//   gap: ${({ $isMobile }) => ($isMobile ? '0.5rem' : '1rem')};
//   width: 100%;
//   max-width: 100%;
//   margin: 0;
// `;
const ChatInputArea = styled.div<{ $isMobile: boolean; $isChatActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isMobile }) => ($isMobile ? '0.5rem' : '1rem')};
  border: none;
  border-radius: 12px;
  padding: 0.5rem;
  background: transparent;

  width: ${({ $isMobile }) =>
    $isMobile
      ? '100%' /* 모바일에서 버튼이 없어졌으니 전체 너비 사용 */
      : '60vw'};

  max-width: ${({ $isMobile }) =>
    $isMobile
      ? '100%' /* 모바일에서 버튼이 없어졌으니 전체 너비 사용 */
      : '600px'};

  margin: 0 auto;
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: relative;
  z-index: 10;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  svg {
    width: 24px;
    height: 24px;
    pointer-events: none;
    transition: fill 0.2s ease;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.textarea<{ $isRounded: boolean }>`
  flex-grow: 1;
  width: 100%;
  padding: ${({ $isRounded }) =>
    $isRounded ? '12px 1.5rem 12px 2rem' : '10px 1.5rem 10px 2rem'
  }; /* 모바일에서 버튼이 없어졌으니 오른쪽 패딩 줄임 */
  font-size: 13px;
  background-color: transparent;
  color: ${props => props.theme.textDefaultColor};
  border: 2px solid #e1e8f0;
  outline: none;
  resize: none;
  line-height: 1.5;
  max-height: 5em;
  overflow-y: hidden;
  transition: all 0.2s ease;
  z-index: 9;
  font-family: 'Noto Sans KR', sans-serif;
  border-radius: ${({ $isRounded }) => ($isRounded ? '25px' : '12px')};

  &::placeholder {
    color: #95a5c6;
  }

  &:focus:not(:disabled) {
    background-color: transparent;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background-color: #e9ecef;
    color: #6c757d;
    cursor: not-allowed;
    border-color: #ced4da;

    &::placeholder {
      color: #adb5bd;
    }
  }
`;

const MicButtonWrapper = styled.div`
  position: absolute;
  right: 8px; /* 모바일에서 버튼이 없어졌으니 더 안쪽으로 */
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
`;

const MicButton = styled.button<{ $isRecording: boolean; $isDarkMode: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => {
    if (props.$isRecording) {
      return props.$isDarkMode ? 'rgba(255, 107, 107, 0.95)' : 'rgba(255, 59, 48, 0.95)';
    } else {
      return props.$isDarkMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(102, 10, 189, 0.1)';
    }
  }};
  color: ${props => {
    if (props.$isRecording) {
      return '#fff';
    } else {
      return props.$isDarkMode ? 'rgba(139, 92, 246, 0.95)' : 'rgba(102, 10, 189, 0.95)';
    }
  }};
  border: ${props => {
    if (props.$isRecording) {
      return 'none';
    } else {
      return props.$isDarkMode ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(102, 10, 189, 0.2)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$isRecording) {
        return props.$isDarkMode ? 'rgba(255, 107, 107, 1)' : 'rgba(255, 59, 48, 1)';
      } else {
        return props.$isDarkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(102, 10, 189, 0.15)';
      }
    }};
    border-color: ${props => {
      if (props.$isRecording) {
        return 'none';
      } else {
        return props.$isDarkMode ? 'rgba(139, 92, 246, 0.5)' : 'rgba(102, 10, 189, 0.4)';
      }
    }};
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: rgba(173, 181, 189, 0.1);
    color: #adb5bd;
    border: 1px solid rgba(173, 181, 189, 0.2);
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const QuestionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  color: #2c3e50;

  &:hover {
    background-color: rgba(44, 62, 80, 0.1);
  }

  svg {
    width: 3rem;
    height: 3rem;
    fill: currentColor;
  }
`;



// 음성인식 시각화 효과 스타일
const VoiceVisualizerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5000;
  backdrop-filter: blur(3px);
`;

const VoiceVisualizer = styled.div`
  background: ${props => props.theme.bodyBgColor};
  border: none;
  border-radius: 20px;
  padding: 2rem;
  width: 80%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const VisualizerText = styled.div`
  font-size: 1.6rem;
  font-weight: 500;
  color: ${props => props.theme.textDefaultColor};
  margin-bottom: 2rem;
`;

const waveAnimation = keyframes`
  0% { height: 5px; }
  50% { height: 70px; }
  100% { height: 5px; }
`;

const WaveContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  width: 100%;
  margin-bottom: 2rem;
`;

const WaveBar = styled.div<{ $delay: number; $intensity?: number }>`
  width: 4px;
  height: ${props => 5 + (props.$intensity || 1) * 8}px;
  margin: 0 2px;
  background: linear-gradient(to bottom, ${props => props.theme.logoColor}, ${props => props.theme.btnColor});
  border-radius: 2px;
  animation: ${css`${waveAnimation} 1.2s ease-in-out infinite`};
  animation-delay: ${props => props.$delay}s;
  transform: scaleY(${props => 0.5 + (props.$intensity || 1) * 0.5});
  transition: height 0.2s ease, transform 0.2s ease;
`;

const AudioLevelIndicator = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 10px 0;
  padding: 8px;
  background: ${props => props.theme.questionBoxBg};
  border-radius: 8px;
`;

const AudioLevelBar = styled.div<{ $level: number }>`
  width: ${props => props.$level}%;
  height: 6px;
  background: linear-gradient(90deg, 
    #4ade80 0%, 
    #22c55e 30%, 
    #facc15 60%, 
    #ef4444 90%
  );
  border-radius: 3px;
  transition: width 0.1s ease;
  margin-bottom: 5px;
`;

const AudioLevelText = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textThinColor};
  text-align: center;
`;

const VisualizerButton = styled.button`
  padding: 0.8rem 2.5rem;
  background: ${props => props.theme.logoColor};
  color: ${props => props.theme.whiteText};
  border: none;
  border-radius: 30px;
  font-size: 1.4rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1a3570;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const GuideButtonLeft = styled.button`
  background: linear-gradient(135deg, #4285f4 0%, #3367d6 100%);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: linear-gradient(135deg, #3367d6 0%, #2c5aa0 100%);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.4);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
    filter: drop-shadow(0 1px 2px rgba(255, 255, 255, 0.3));
  }
`;